import { Controller, useForm, useFormContext } from "react-hook-form";
import type { SxProps, Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Typography } from "ui/typography";
import { FormDialog } from "ui/form-dialog";
import { TextArea } from "ui/text-area";

import StrategyLink from "components/modals/shared/strategy-link";

import type {
  RefineCreativesContentFormValues,
  RefineCreativesContentModalProps,
} from "components/modals/refine-creatives-content-modal/refine-creatives-content-modal.types";

const LABEL_SX: SxProps<Theme> = { fontWeight: 700 };

const STRATEGY_LINKS_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  width: "100%",
};

const RefineCreativesContentFormFields = ({
  programStrategyLabel,
  programStrategyHref,
  campaignStrategyLabel,
  campaignStrategyHref,
}: Pick<
  RefineCreativesContentModalProps,
  | "programStrategyLabel"
  | "programStrategyHref"
  | "campaignStrategyLabel"
  | "campaignStrategyHref"
>) => {
  const { control } = useFormContext<RefineCreativesContentFormValues>();

  return (
    <>
      <Controller
        name="creativesAdditionalInstructions"
        control={control}
        render={({ field: { onChange, value, ...field } }) => (
          <TextArea
            label={
              <Typography component="span" variant="inherit" sx={LABEL_SX}>
                Additional instructions
              </Typography>
            }
            value={value || ""}
            onChange={(event) => onChange(event.target.value)}
            rows={5}
            {...field}
          />
        )}
      />

      <Box sx={STRATEGY_LINKS_SX}>
        <StrategyLink
          label="Program strategy:"
          href={programStrategyHref}
          linkLabel={programStrategyLabel}
        />
        <StrategyLink
          label="Campaign strategy:"
          href={campaignStrategyHref}
          linkLabel={campaignStrategyLabel}
        />
      </Box>
    </>
  );
};

const RefineCreativesContentModal = ({
  onCloseCallback,
  onConfirmCallback,
  programStrategyLabel,
  programStrategyHref,
  campaignStrategyLabel,
  campaignStrategyHref,
  isSubmitting = false,
}: RefineCreativesContentModalProps) => {
  const form = useForm<RefineCreativesContentFormValues>({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: { creativesAdditionalInstructions: "" },
  });

  return (
    <FormDialog
      open
      onClose={onCloseCallback}
      headline="Refine creatives content"
      description="Enter custom prompt for this specific creatives and describe how you would like to improve context text."
      form={form}
      onSubmit={onConfirmCallback}
      submitting={isSubmitting}
      submitButtonText="Rewrite"
      submitLoadingMessage="Rewriting...">
      <RefineCreativesContentFormFields
        programStrategyLabel={programStrategyLabel}
        programStrategyHref={programStrategyHref}
        campaignStrategyLabel={campaignStrategyLabel}
        campaignStrategyHref={campaignStrategyHref}
      />
    </FormDialog>
  );
};

export default RefineCreativesContentModal;
